import { BRAND } from "constants/brand";
import DateTimeFilter from "components/Ui/DateTimeFilter/DateTimeFilter";
import { Ring } from "@uiball/loaders";
import { Form } from "react-bootstrap";
import { NavLink, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Select from "components/Ui/SelectField/SelectField";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";

import { useListStatements } from "hooks/useStatements";
import { useGetVendorsDropdown } from "hooks/useVendors";
import { formatStatNumber } from "utils/percentage";
import { formatMoney } from "utils/money";
import TableColumnsMenu from "components/Ui/TableColumnsMenu/TableColumnsMenu";
import TableHead from "components/Ui/TableHead/TableHead";
import TableBodyCells from "components/Ui/TableBodyCells/TableBodyCells";
import useTableColumnsConfig from "hooks/useTableColumnsConfig";
import { formatDate, formatDateTime as formatDateTimeOrNull } from "utils/dateFormat";

/**
 * Column definition for the statements list table. Each id matches a key
 * in the `cellRenderers` map inside the row render. The columns menu lets
 * the admin hide/reorder anything except… (no pinned columns for now).
 */
const STATEMENT_COLUMNS = [
    { id: "id", label: "#", width: "60px" },
    { id: "vendor", label: "المطعم", width: "140px" },
    { id: "period", label: "الفترة", width: "180px" },
    { id: "ordersCount", label: "عدد الطلبات", width: "100px" },
    { id: "subtotal", label: "إجمالي المبيعات (₪)", width: "140px" },
    { id: "commission", label: "العمولة (₪)", width: "120px" },
    { id: "netAmount", label: "الصافي (₪)", width: "120px" },
    { id: "paymentStatus", label: "حالة الدفع", width: "120px" },
    { id: "whatsappStatus", label: "حالة الواتساب", width: "120px" },
    { id: "sentAt", label: "تاريخ الإرسال", width: "160px" },
    { id: "actions", label: "", width: "60px" },
];

/**
 * Account statements list page (`/statements`). Visible to:
 * - admins — full list with vendor + city filters
 * - vendor users — auto-filtered to their own statements (the API
 *   ignores any vendor/city query params from vendor callers and forces
 *   `audience='vendor'` + their own `vendor_id`)
 *
 * Each row links to the detail view at `/statements/:id`.
 */
const Statements = () => {
    const adminInfo = useSelector((state) => state.auth.userData);
    const [searchParams, setSearchParams] = useSearchParams();
    const [page, setPage] = useState(1);

    const isVendor = adminInfo?.role === "vendor";

    const selectedCity = searchParams.get("city");
    const resolvedCityId = adminInfo?.super_admin ? selectedCity || undefined : adminInfo?.cityId;
    const cities = useSelector((state) => state.city.cities);

    // Vendors don't need the dropdown — their list is auto-scoped server-side.
    const { vendors } = useGetVendorsDropdown(
        isVendor ? { cityId: undefined } : { cityId: resolvedCityId },
    );

    const params = {
        audience: "vendor",
        page,
        size: 12,
    };
    if (!isVendor && searchParams.get("vendor")) {
        params.vendorId = searchParams.get("vendor");
    }
    if (searchParams.get("startDate")) {
        params.startDate = searchParams.get("startDate");
    }
    if (searchParams.get("endDate")) {
        params.endDate = searchParams.get("endDate");
    }
    if (!isVendor && resolvedCityId) {
        params.cityId = resolvedCityId;
    }
    // Payment-status filter — three states, defaulting to UNPAID only so the
    // admin lands on "what still needs settling". Mirrors the deletedForVendor
    // convention: missing/empty → default (unpaid), "all" → no filter,
    // "true" → paid only.
    const isPaidFilter = searchParams.get("isPaid");
    if (isPaidFilter === "true") {
        params.isPaid = "true";
    } else if (isPaidFilter !== "all") {
        params.isPaid = "false";
    }

    const { statements, count, pages, loading, paidVisibleDays } = useListStatements(params);
    // A paid statement leaves the list some days after its payment. The two
    // choices that show paid statements say it, with the API's own number.
    const paidWindow = paidVisibleDays ? `آخر ${paidVisibleDays} يومًا` : null;

    // Vendors only ever see unpaid statements, so the payment-status
    // column would be a redundant all-"غير مدفوع" column for them — drop it.
    const columns = isVendor
        ? STATEMENT_COLUMNS.filter((c) => c.id !== "paymentStatus")
        : STATEMENT_COLUMNS;
    const { orderedColumns, visibleColumns, hiddenSet, toggleColumn, moveColumn, reset } =
        useTableColumnsConfig("statements", columns);

    const vendorOptions = (vendors ?? []).map((v) => ({ value: v.id, label: v.name }));

    return (
        <div className="items">
            {loading ? (
                <div className="loadingSpinner">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : (
                <>
                    <div
                        className="row row-cols-2 row-cols-md-3 filters"
                        style={{ padding: "20px", alignItems: "center" }}
                    >
                        {!isVendor ? (
                            <div className="col">
                                <h6>المطعم</h6>
                                <Select
                                    options={[{ value: "", label: "الكل" }, ...vendorOptions]}
                                    placeholder="اختر المطعم"
                                    defaultValue={
                                        vendorOptions.find(
                                            (v) => v.value === +searchParams.get("vendor"),
                                        ) || { value: "", label: "الكل" }
                                    }
                                    isSearchable
                                    onChange={(selected) => {
                                        if (!selected || selected.value === "") {
                                            searchParams.delete("vendor");
                                        } else {
                                            searchParams.set("vendor", selected.value);
                                        }
                                        setSearchParams(searchParams);
                                        setPage(1);
                                    }}
                                />
                            </div>
                        ) : null}
                        {adminInfo?.super_admin ? (
                            <div className="col">
                                <h6>المدينه</h6>
                                <Form.Select
                                    defaultValue={searchParams.get("city") || ""}
                                    onChange={(e) => {
                                        if (e.target.value === "") {
                                            searchParams.delete("city");
                                        } else {
                                            searchParams.set("city", e.target.value);
                                        }
                                        setSearchParams(searchParams);
                                        setPage(1);
                                    }}
                                >
                                    <option value={""}>الكل</option>
                                    {cities?.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                        ) : null}
                        <DateTimeFilter
                            paramKey="startDate"
                            label="من"
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            onApplied={() => setPage(1)}
                        />
                        <DateTimeFilter
                            paramKey="endDate"
                            label="إلى"
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            onApplied={() => setPage(1)}
                        />
                        {/* Payment-status filter is admin-only. Vendors are
                            forced to unpaid-only server-side, so the filter
                            would do nothing for them. Default (empty) = unpaid
                            only; "all" = no filter; "true" = paid only. */}
                        {!isVendor ? (
                            <div className="col" style={{ marginTop: "15px" }}>
                                <h6>حالة الدفع</h6>
                                <Form.Select
                                    defaultValue={searchParams.get("isPaid") || ""}
                                    onChange={(e) => {
                                        if (e.target.value === "") {
                                            searchParams.delete("isPaid");
                                        } else {
                                            searchParams.set("isPaid", e.target.value);
                                        }
                                        setSearchParams(searchParams);
                                        setPage(1);
                                    }}
                                >
                                    <option value="">غير المدفوعة فقط</option>
                                    <option value="all">
                                        {paidWindow ? `الكل (المدفوعة: ${paidWindow})` : "الكل"}
                                    </option>
                                    <option value="true">
                                        {paidWindow
                                            ? `المدفوعة فقط (${paidWindow})`
                                            : "المدفوعة فقط"}
                                    </option>
                                </Form.Select>
                            </div>
                        ) : null}
                    </div>

                    <div
                        className="controls"
                        style={{ display: "flex", justifyContent: "space-between" }}
                    >
                        <div className="stats-container2" style={{ flex: 1 }}>
                            <div className="stat-box">
                                <h4>عدد الكشوف</h4>
                                <p>{formatStatNumber(count)}</p>
                            </div>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                marginRight: "auto",
                            }}
                        >
                            <TableColumnsMenu
                                orderedColumns={orderedColumns}
                                hiddenSet={hiddenSet}
                                toggleColumn={toggleColumn}
                                moveColumn={moveColumn}
                                reset={reset}
                            />
                        </div>
                    </div>

                    <div className="table-container">
                        <table>
                            <TableHead visibleColumns={visibleColumns} />
                            {statements.length ? (
                                <tbody>
                                    {statements.map((s) => (
                                        <StatementRow
                                            key={s.id}
                                            statement={s}
                                            visibleColumns={visibleColumns}
                                        />
                                    ))}
                                </tbody>
                            ) : (
                                <tbody>
                                    <tr>
                                        <td colSpan={visibleColumns.length || 1}>
                                            <h4 style={{ textAlign: "center", padding: "30px" }}>
                                                لا يوجد كشوف
                                            </h4>
                                        </td>
                                    </tr>
                                </tbody>
                            )}
                        </table>
                    </div>

                    <Stack spacing={1}>
                        <Pagination
                            count={pages}
                            page={page}
                            shape="rounded"
                            color={BRAND}
                            style={{ margin: "20px auto 0" }}
                            onChange={(_e, newPage) => setPage(newPage)}
                        />
                    </Stack>
                </>
            )}
        </div>
    );
};

/**
 * Renders one statement row by mapping the visible column ids to per-cell
 * renderers. Keeping this co-located with the parent so the columns list
 * stays the single source of truth for what fields appear.
 */
const StatementRow = ({ statement, visibleColumns }) => {
    const cellRenderers = {
        id: () => <td>{statement.id}</td>,
        vendor: () => <td>{statement.vendor?.user?.name || "—"}</td>,
        period: () => (
            <td>
                <span dir="ltr" style={{ unicodeBidi: "isolate", whiteSpace: "nowrap" }}>
                    {formatPeriod(statement.start_date)}
                    {" → "}
                    {formatPeriod(statement.end_date)}
                </span>
            </td>
        ),
        ordersCount: () => <td>{formatStatNumber(statement.orders_count)}</td>,
        subtotal: () => <td>{formatMoney(statement.subtotal)}</td>,
        commission: () => <td>{formatMoney(statement.commission)}</td>,
        netAmount: () => <td>{formatMoney(statement.net_amount)}</td>,
        paymentStatus: () => (
            <td>
                <PaymentStatusBadge isPaid={statement.is_paid} />
            </td>
        ),
        whatsappStatus: () => (
            <td>
                <WhatsappStatusBadge status={statement.whatsapp_send_status} />
            </td>
        ),
        sentAt: () => <td>{statement.sent_at ? formatDateTime(statement.sent_at) : "—"}</td>,
        actions: () => (
            <td>
                <NavLink
                    to={`/statements/${statement.id}`}
                    style={{
                        color: "blue",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                    }}
                >
                    <FontAwesomeIcon icon={faEye} />
                    عرض
                </NavLink>
            </td>
        ),
    };

    return (
        <tr>
            <TableBodyCells visibleColumns={visibleColumns} renderers={cellRenderers} />
        </tr>
    );
};

/**
 * Renders a statement's payment status as a colored pill. Shared by the
 * list-page row and the detail-page header so the badge looks the same
 * everywhere.
 */
export const PaymentStatusBadge = ({ isPaid }) => (
    <span
        style={{
            background: isPaid ? "#28a745" : "#dc3545",
            color: "#fff",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "0.85em",
            whiteSpace: "nowrap",
        }}
    >
        {isPaid ? "مدفوع" : "غير مدفوع"}
    </span>
);

const WhatsappStatusBadge = ({ status }) => {
    const colors = {
        sent: "#28a745",
        pending: "#ffc107",
        failed: "#dc3545",
    };
    const labels = {
        sent: "تم الإرسال",
        pending: "قيد الإرسال",
        failed: "فشل الإرسال",
    };
    return (
        <span
            style={{
                background: colors[status] ?? "#6c757d",
                color: "#fff",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "0.85em",
            }}
        >
            {labels[status] ?? status}
        </span>
    );
};

const formatPeriod = (value) => formatDate(value) ?? "";

const formatDateTime = (value) => formatDateTimeOrNull(value) ?? "";

export default Statements;
